import * as React from "react";

import { CommandPalette } from "@/components/CommandPalette";
import { HelpDrawer } from "@/components/help/HelpDrawer";
import { Breadcrumbs } from "@/components/layout/Breadcrumbs";
import { MobileNav } from "@/components/layout/MobileNav";
import { PrimarySidebar } from "@/components/layout/PrimarySidebar";
import { TopBar } from "@/components/layout/TopBar";
import { WelcomeModal } from "@/components/onboarding/WelcomeModal";
import { useGlobalShortcuts } from "@/hooks/useGlobalShortcuts";
import { useShouldShowBreadcrumbs } from "@/hooks/useNavRoutes";
import { useNotificationSubscription } from "@/hooks/useNotificationSubscription";
import { isFirstTime } from "@/lib/onboarding";
import { useAuthStore } from "@/stores/auth";
import { useOnboardingStore } from "@/stores/onboarding";
import { useRouteStore } from "@/stores/route";

interface AppShellProps {
  children: React.ReactNode;
}

/**
 * Root layout wrapper khi user authenticated.
 *
 * Layout (≥ 768px desktop):
 * ┌────┬───────────────────────────────────────┐
 * │ Pr │  TopBar slim                          │
 * │ Si ├───────────────────────────────────────┤
 * │ de │  Breadcrumbs (non-chat, non-editor)   │
 * │ ba ├───────────────────────────────────────┤
 * │ r  │  children                             │
 * └────┴───────────────────────────────────────┘
 *
 * Mobile (< 768px):
 * - PrimarySidebar ẩn
 * - MobileNav bottom tabs
 * - children còn `pb-14` extra cho space tabs
 *
 * Full-screen editors (FormBuilder/WorkflowDesigner) tự render header
 * riêng + KHÔNG có breadcrumbs (filter trong useShouldShowBreadcrumbs).
 *
 * Doc 28 §4 + Doc 29 §5.
 */
export function AppShell({ children }: AppShellProps): JSX.Element {
  const showBreadcrumbs = useShouldShowBreadcrumbs();
  const route = useRouteStore((s) => s.current);
  const isFullScreenEditor =
    route === "admin.forms.edit" || route === "admin.workflows.edit";

  // Global shortcuts: Cmd+K (palette, S4), Cmd+/ (help, S6)
  useGlobalShortcuts();

  // P3-S3: realtime notification WS push (replaces 30s polling for active users)
  useNotificationSubscription();

  // First-time welcome (P2.1-S6) — chỉ show 1 lần khi authenticated
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const openWelcome = useOnboardingStore((s) => s.openWelcome);
  React.useEffect(() => {
    if (isAuthenticated && isFirstTime()) {
      // Defer 400ms cho user kịp load page trước khi modal pop up
      const t = window.setTimeout(() => openWelcome(), 400);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [isAuthenticated, openWelcome]);

  // Chat tự xử lý overflow trong ChatStream (chat input phải dính bottom).
  // Các page khác không tự handle → main wrap thành overflow-y-auto.
  const isChatRoute = route === "chat";

  return (
    <div className="h-full flex bg-background text-foreground overflow-hidden">
      <PrimarySidebar />

      <div className="flex-1 flex flex-col min-w-0 min-h-0">
        {/* Full-screen editors render their own toolbar — skip TopBar to maximize editor space */}
        {!isFullScreenEditor ? <TopBar /> : null}

        {showBreadcrumbs ? <Breadcrumbs /> : null}

        <main
          className={
            "flex-1 min-h-0 flex flex-col pb-14 md:pb-0 " +
            (isChatRoute || isFullScreenEditor ? "" : "overflow-y-auto")
          }
        >
          {children}
        </main>
      </div>

      <MobileNav />

      {/* Mounted globally — works ngay cả ở full-screen editors */}
      <CommandPalette />
      <HelpDrawer />
      <WelcomeModal />
    </div>
  );
}
